import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  Destroyable,
  isSomething,
  Rental,
  Toaster,
  untilDestroy,
  updateRentalPaymentSchedule,
} from '@tokeet/tv3-platform'
import * as R from 'ramda'
import { distinctUntilChanged } from 'rxjs/operators'
import { Store } from '@ngrx/store'

@Component({
  selector: 'app-edit-payment-schedule-dialog',
  templateUrl: './edit-payment-schedule-dialog.component.html',
  styleUrls: ['./edit-payment-schedule-dialog.component.scss'],
})
export class EditPaymentScheduleDialogComponent extends Destroyable implements OnInit {
  termsForm = this.fb.group({
    terms: this.fb.array([]),
  })

  termForm = this.fb.group({
    type: ['', [Validators.required]],
    days: ['', [Validators.required, Validators.min(0)]],
    percent: ['', [Validators.required]],
  })

  termTypes = [
    { value: 'AT_CHECKIN', label: $localize`At Check-in` },
    { value: 'AT_CHECKOUT', label: $localize`At Check-out` },
    { value: 'AFTER_BOOKING', label: $localize`After booking` },
    { value: 'AFTER_CHECKOUT', label: $localize`After Check-out` },
    { value: 'AT_BOOKING', label: $localize`At booking` },
    { value: 'BEFORE_CHECKIN', label: $localize`Before Check-in` },
    { value: 'AFTER_CHECKIN', label: $localize`After Check-in` },
  ]

  get terms() {
    return this.termsForm.get('terms') as FormArray
  }

  get remainingPercentage() {
    const values = this.termsForm.getRawValue()
    const total = R.sum(R.map((t) => t.percent, values.terms))
    return R.times((i) => i + 1, 100 - total)
  }

  get remainingTypes() {
    const values = this.termsForm.getRawValue()
    const usedTypes = R.map((t) => t.type, values.terms)

    return R.reject((type) => R.contains(type.value, usedTypes), this.termTypes)
  }

  constructor(
    public dialogRef: MatDialogRef<EditPaymentScheduleDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: { rental: Rental }
  ) {
    super()
  }

  ngOnInit() {
    R.forEachObjIndexed((value: any, key: any) => {
      const typeView = this.termTypes.find((t) => t.value === key)
      if (key === 'AT_BOOKING' && value.percent === 0) {
        console.log('skip')
        return
      }
      if (isSomething(value.days)) {
        this.terms.push(
          this.fb.group({
            type: [{ value: key, disabled: true }, [Validators.required]],
            typeView: [{ value: typeView.label, disabled: true }, [Validators.required]],
            days: [{ value: value.days, disabled: true }, [Validators.required]],
            percent: [{ value: value.percent, disabled: true }, [Validators.required]],
          })
        )
      } else {
        this.terms.push(
          this.fb.group({
            type: [{ value: key, disabled: true }, [Validators.required]],
            typeView: [{ value: typeView.label, disabled: true }, [Validators.required]],
            percent: [{ value: value.percent, disabled: true }, [Validators.required]],
          })
        )
      }
    }, this.data.rental.payment_schedule)

    this.termForm
      .get('type')
      .valueChanges.pipe(distinctUntilChanged(), untilDestroy(this))
      .subscribe((type) => {
        if (this.hasDays(type)) {
          this.termForm.get('days').setValidators([Validators.required, Validators.min(0)])
          this.termForm.get('days').updateValueAndValidity()
        } else {
          this.termForm.get('days').setValidators([])
          this.termForm.get('days').updateValueAndValidity()
        }
      })
  }

  hasDays(type: string) {
    return (
      type === 'AFTER_CHECKOUT' || type === 'BEFORE_CHECKIN' || type === 'AFTER_CHECKIN' || type === 'AFTER_BOOKING'
    )
  }

  close() {
    this.dialogRef.close()
  }

  canAddMoreTypes(controls: any[]) {
    const hasAtBooking = !!controls.find((control) => {
      const { percent, type } = control.value
      return type === 'AT_BOOKING' // && percent === 0
    })
    const max = hasAtBooking ? 5 : 4
    return controls?.length < max
  }

  onAddTerm() {
    if (this.termForm.invalid) {
      this.termForm.markAllAsTouched()
      return
    }
    const values = this.termForm.getRawValue()

    const typeView = this.termTypes.find((t) => t.value === values.type)

    this.terms.push(
      this.fb.group({
        type: [{ value: values.type, disabled: true }, [Validators.required]],
        typeView: [{ value: typeView.label, disabled: true }, [Validators.required]],
        days: [{ value: values.days, disabled: true }, [Validators.required]],
        percent: [{ value: values.percent, disabled: true }, [Validators.required]],
      })
    )

    this.termForm.reset()
  }

  onRemoveTerm(index: number) {
    this.terms.removeAt(index)
  }

  onSave() {
    if (this.termsForm.invalid) {
      this.termsForm.markAllAsTouched()
      return
    }

    if (this.termsForm.get('terms').value?.length === 0) {
      this.toaster.error($localize`You must set at least one payment term.`)
      return
    }

    const { terms } = this.termsForm.getRawValue()
    const payment_schedule = {
      ...R.pick(['type'], terms),
    }
    R.forEachObjIndexed((value, key) => {
      if (isSomething(value.days)) {
        payment_schedule[value.type] = { days: value.days, percent: value.percent }
      } else {
        payment_schedule[value.type] = { percent: value.percent }
      }
    }, terms)

    const hasAtBooking = !!payment_schedule['AT_BOOKING']
    if (!hasAtBooking) {
      payment_schedule['AT_BOOKING'] = {
        percent: 0,
      }
    }

    const payload = {
      payment_schedule,
    }

    this.store.dispatch(updateRentalPaymentSchedule({ payload, rental: this.data.rental }))
  }
}
